import CheckIcon from '@mui/icons-material/Check';
import { Stack, Typography } from '@mui/material';
import { some } from 'lodash';
import { useMemo } from 'react';

import { DissolvedOxygenDatum, useDissolvedOxygenData } from '@/api/historicalData';
import { dissolvedOxygenIcon } from '@/assets';
import { Line } from '@/charts';
import { colorsets } from '@/charts/common';
import { degradedDotRenderer, getYValue } from '@/charts/helpers';
import { ErrorIcon } from '@/components/StatusIcons';
import { LineIcon } from '@/components/data/ColorIcon';
import { Datum, unit, unitType } from '@/components/data/Datum';
import { LegendItem } from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { SensorLineChartSection } from '@/components/spotter/historicalDataTab/SensorLineChartSection';
import { DataDetailDatum } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { minorTextGrey } from '@/theme';

const MIN_GOOD_DO_QUALITY_RATIO = 0.7;

const doDatumDetailRenderer: Line<DissolvedOxygenDatum>['datumDetailRenderer'] = (
    datum,
    { label, color, valueKey, ...units }
) => {
    const good = datum.quality >= MIN_GOOD_DO_QUALITY_RATIO;
    return (
        <Stack>
            <DataDetailDatum label={label} icon={<LineIcon dot color={color.line} />}>
                <Datum
                    value={getYValue(datum, valueKey)}
                    type={unitType(units, datum)}
                    sourceUnit={unit(units, datum)}
                />
            </DataDetailDatum>
            <DataDetailDatum label="Quality">
                {good ? (
                    <CheckIcon fontSize="inherit" htmlColor={color.line} />
                ) : (
                    <ErrorIcon fontSize="inherit" color="warning" />
                )}
                <Typography>
                    {good ? 'Good' : 'Poor'} (<Datum value={datum.quality} type="unitless" />)
                </Typography>
            </DataDetailDatum>
        </Stack>
    );
};

const doDotRenderer = degradedDotRenderer<DissolvedOxygenDatum>((datum) => datum.quality < MIN_GOOD_DO_QUALITY_RATIO);

export const LINE_CONCENTRATION: Line<DissolvedOxygenDatum> = {
    id: 'dissolved-oxygen-concentration',
    label: 'Concentration',
    valueKey: 'concentration',
    unitKey: 'concentrationUnits',
    unitTypeKey: 'concentrationUnitType',
    dotRenderer: doDotRenderer,
    datumDetailRenderer: doDatumDetailRenderer,
    color: colorsets.blue, // placeholder for type; will be replaced per sensor
};

const LINE_SATURATION: Line<DissolvedOxygenDatum> = {
    id: 'dissolved-oxygen-saturation',
    label: 'Saturation',
    valueKey: 'saturation',
    unitKey: 'saturationUnits',
    unitTypeKey: 'saturationUnitType',
    dotRenderer: doDotRenderer,
    datumDetailRenderer: doDatumDetailRenderer,
    color: colorsets.blue, // placeholder for type; will be replaced per sensor
};

export const DissolvedOxygenSection = () => {
    const { spotterId } = useSpotterContext();
    const { dateRange } = useDateRangeContext();

    const sensorData = useDissolvedOxygenData({
        spotterId: spotterId!,
        dateRange,
    });

    const hasSaturation = useMemo(
        () =>
            some(Object.values(sensorData?.data ?? {}), (positionData) => some(positionData, (d) => d.saturation > 0)),
        [sensorData?.data]
    );

    const legend = useMemo(() => {
        const hasHighQuality = some(Object.values(sensorData?.data ?? {}), (positionData) =>
            some(positionData, (d) => d.quality >= MIN_GOOD_DO_QUALITY_RATIO)
        );
        const hasLowQuality = some(Object.values(sensorData?.data ?? {}), (positionData) =>
            some(positionData, (d) => d.quality < MIN_GOOD_DO_QUALITY_RATIO)
        );

        if (hasHighQuality && hasLowQuality) {
            return (
                <>
                    <Typography variant="body2" sx={{ color: minorTextGrey }}>
                        Measurement quality ratio:
                    </Typography>
                    <LegendItem icon={<LineIcon color="black" dot />}>Good (0.75-1.0)</LegendItem>
                    <LegendItem icon={<LineIcon color="black" emptyDot />}>Poor (0.0-0.75)</LegendItem>
                </>
            );
        }
    }, [sensorData?.data]);

    return (
        <>
            <SensorLineChartSection
                chartId="dissolved-oxygen-concentration"
                chartTitle="Dissolved oxygen concentration"
                yAxisLabel="Dissolved oxygen"
                sensorType="dissolvedOxygen"
                sensorData={sensorData}
                baseLine={LINE_CONCENTRATION}
                sensorIcon={{
                    src: dissolvedOxygenIcon,
                }}
                legend={legend}
                alertLineId="smartmooring_threshold_dissolved_oxygen_concentration"
            />
            {hasSaturation && (
                <SensorLineChartSection
                    chartId="dissolved-oxygen-saturation"
                    chartTitle="Dissolved oxygen saturation"
                    yAxisLabel="Dissolved oxygen"
                    sensorType="dissolvedOxygen"
                    sensorData={sensorData}
                    baseLine={LINE_SATURATION}
                    sensorIcon={{
                        src: dissolvedOxygenIcon,
                    }}
                    legend={legend}
                    alertLineId="smartmooring_threshold_dissolved_oxygen_saturation"
                />
            )}
        </>
    );
};
